import React, { useRef } from "react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { Favorite, NewspaperOutlined } from "@mui/icons-material";
import { useLocationAllowed } from "./LocationProvider";

interface LocationData {
  country_code: string;
  country_name: string;
  city: string;
  region: string;
}

// Google Analytics tracking function
const trackDonationClick = () => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "donation_click", {
      event_category: "engagement",
      event_label: "donation_button",
      value: 1,
    });
  }
};

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

interface DonationComponentProps {
  title?: string;
  description?: string;
  buttonText?: string;
  donationUrl?: string;
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  showIcon?: boolean;
}

export default function DonationArticle({
  title = "Поддржете Независно Новинарство",
  description = "Вашиот придонес ни помага да продолжиме со објективно и темелно известување што е важно. Секоја донација, без оглед на големината, придонесува за одржување на независноста на нашата редакција и овозможување бесплатна содржина за сите.",
  buttonText = "Поддржете нѐ: Донирајте Сега",
  donationUrl = "https://www.gofundme.com/f/mevv7-donirajte-za-nasiot-medium-da-ostane-nezavisen",
  backgroundColor = "#f8f9fa",
  primaryColor = "#1976d2",
  secondaryColor = "#dc004e",
  showIcon = true,
}: DonationComponentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAllowed, loading } = useLocationAllowed();
  const handleDonateClick = () => {
    trackDonationClick();
  };
  const cardRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (loading || !isAllowed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;

          if (typeof window !== "undefined" && window.gtag) {
            window.gtag("event", "donation_component_view", {
              event_category: "engagement",
              event_label: "donation_component",
              value: 1,
            });
          }

          observer.disconnect(); // stop observing after first view
        }
      },
      { threshold: 0.5 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [loading, isAllowed]);
  if (loading || !isAllowed) return null;
  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Card
        ref={cardRef}
        elevation={0}
        variant="outlined"
        sx={{
          backgroundColor,
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems="center"
            gap={3}
          >
            {showIcon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 60, md: 80 },
                  height: { xs: 60, md: 80 },
                  borderRadius: "50%",
                  backgroundColor: primaryColor,
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <NewspaperOutlined sx={{ fontSize: { xs: 30, md: 40 } }} />
              </Box>
            )}

            <Box flex={1} textAlign={isMobile ? "center" : "left"}>
              <Typography
                variant={"h5"}
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a1a",
                  mb: 2,
                }}
              >
                {title}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  lineHeight: 1.6,
                  fontSize: { xs: "0.95rem", md: "1rem" },
                }}
              >
                {description}
              </Typography>

              <a href={donationUrl} target="_blank">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDonateClick}
                  startIcon={<Favorite />}
                  sx={{
                    backgroundColor: secondaryColor,
                    color: "white",
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: "bold",
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: `0 4px 12px ${secondaryColor}40`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: secondaryColor,
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px ${secondaryColor}50`,
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  {buttonText}
                </Button>
              </a>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
